/*
 * Seitenleiste des Editors: links die Bausteine, rechts die Eigenschaften.
 *
 * Wunsch von Patrick (04.11.2026): beide Seiten einklappbar, mit Pin, und
 * die Breite der Bausteinleiste mit einem Griff einstellbar, die Spaltenzahl
 * folgt der Breite.
 *
 * - Angeheftet: die Leiste belegt ihre Spalte, die Flaeche rueckt zur Seite.
 * - Nicht angeheftet: es bleibt ein schmaler Streifen. Ein Klick darauf (oder
 *   mit der Maus darueber verweilen) legt die Leiste UEBER die Flaeche; sie
 *   schliesst sich, wenn die Maus sie verlaesst oder Esc kommt. So bekommt
 *   die Flaeche die volle Breite und man holt die Leiste nur zum Greifen.
 * - Der Griff an der Innenkante zieht die Breite, Doppelklick setzt zurueck.
 *
 * Pin und Breite gelten je Browser (localStorage), nicht je Projekt: das ist
 * eine Gewohnheit des Autors und keine Eigenschaft des Bildes.
 */

import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import './SideDock.css';

export interface SideDockProps {
  side: 'left' | 'right';
  title: string;
  /** Schluessel fuer Pin und Breite im Browser. */
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Zusaetzliche Knoepfe im Kopf, links vom Pin. */
  actions?: ComponentChildren;
  /** Die Leiste schliesst ihre ueberlagerte Ansicht, wenn sich dieser Wert
   *  aendert (etwa nach dem Aufnehmen eines Bausteins). */
  closeSignal?: unknown;
  children: ComponentChildren;
}

function readStore(key: string): { pinned?: boolean; width?: number } {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as { pinned?: boolean; width?: number }) : {};
  } catch {
    return {};
  }
}

function writeStore(key: string, value: { pinned: boolean; width: number }): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* privat oder voll: dann eben nicht */ }
}

export function SideDock(props: SideDockProps) {
  const stored = readStore(props.storageKey);
  // Ohne gespeicherte Wahl: angeheftet, wenn neben zwei Leisten noch eine
  // brauchbare Flaeche bleibt; auf schmalen Schirmen eingeklappt.
  const [pinned, setPinned] = useState(stored.pinned ?? (typeof window === 'undefined' || window.innerWidth >= 1200));
  const [width, setWidth] = useState(() => clamp(stored.width ?? props.defaultWidth, props.minWidth, props.maxWidth));
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef<number | undefined>(undefined);
  const dragging = useRef(false);
  /** Ende des letzten Ziehens. Beim Loslassen ausserhalb der Leiste meldet
   *  der Browser ein Verlassen, das kein Wegbewegen ist. */
  const dragEnd = useRef(0);

  useEffect(() => { writeStore(props.storageKey, { pinned, width }); }, [props.storageKey, pinned, width]);

  // Nach dem Aufnehmen eines Bausteins zurueck auf die Flaeche.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (!pinned) setOpen(false);
  }, [props.closeSignal]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const showPanel = pinned || open;

  function startResize(e: PointerEvent): void {
    e.preventDefault();
    const handle = e.currentTarget as HTMLElement;
    handle.setPointerCapture(e.pointerId);
    dragging.current = true;
    const startX = e.clientX;
    const startW = width;
    const sign = props.side === 'left' ? 1 : -1;
    const move = (ev: PointerEvent) => {
      setWidth(clamp(startW + sign * (ev.clientX - startX), props.minWidth, props.maxWidth));
    };
    const up = () => {
      dragging.current = false;
      dragEnd.current = Date.now();
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  }

  function onResizeKey(e: KeyboardEvent): void {
    const step = e.shiftKey ? 64 : 16;
    const grow = props.side === 'left' ? 'ArrowRight' : 'ArrowLeft';
    const shrink = props.side === 'left' ? 'ArrowLeft' : 'ArrowRight';
    if (e.key === grow) { e.preventDefault(); setWidth((w) => clamp(w + step, props.minWidth, props.maxWidth)); }
    if (e.key === shrink) { e.preventDefault(); setWidth((w) => clamp(w - step, props.minWidth, props.maxWidth)); }
  }

  return (
    <aside
      class={`hv-dock hv-dock--${props.side}${pinned ? ' hv-dock--pinned' : ' hv-dock--rail'}${open && !pinned ? ' hv-dock--open' : ''}`}
      style={{ '--hv-dock-w': `${width}px` }}
      aria-label={props.title}
      onMouseLeave={() => {
        window.clearTimeout(hoverTimer.current);
        if (!pinned && open && !dragging.current && Date.now() - dragEnd.current > 400) setOpen(false);
      }}
    >
      {!pinned && (
        <button
          type="button"
          class="hv-dock__rail"
          aria-expanded={open}
          title={`${props.title} zeigen`}
          onClick={() => setOpen((o) => !o)}
          onMouseEnter={() => {
            window.clearTimeout(hoverTimer.current);
            hoverTimer.current = window.setTimeout(() => setOpen(true), 250);
          }}
          onMouseLeave={() => window.clearTimeout(hoverTimer.current)}
        >
          <span class="hv-dock__railLabel">{props.title}</span>
        </button>
      )}

      {showPanel && (
        <div class="hv-dock__panel">
          <div class="hv-dock__head">
            <span class="hv-dock__title">{props.title}</span>
            {props.actions}
            <button
              type="button"
              class={`hv-dock__pin${pinned ? ' is-on' : ''}`}
              aria-pressed={pinned}
              title={pinned ? 'Lösen: die Leiste klappt ein und öffnet sich über der Fläche' : 'Anheften: die Leiste bleibt offen neben der Fläche'}
              onClick={() => { setPinned((p) => !p); setOpen(false); }}
            >
              <PinIcon />
            </button>
          </div>
          <div class="hv-dock__body">{props.children}</div>
          <div
            class="hv-dock__grip"
            role="separator"
            aria-orientation="vertical"
            aria-label={`Breite ${props.title}`}
            aria-valuenow={width}
            aria-valuemin={props.minWidth}
            aria-valuemax={props.maxWidth}
            tabIndex={0}
            title="Ziehen ändert die Breite, Doppelklick setzt sie zurück"
            onPointerDown={startResize}
            onDblClick={() => setWidth(props.defaultWidth)}
            onKeyDown={onResizeKey}
          />
        </div>
      )}
    </aside>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, Math.round(v)));
}

function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M6 1.5h4l-.6 4.2 2.6 2.3v1.5H8.7V15L8 15.8 7.3 15V9.5H4V8l2.6-2.3z" fill="currentColor" />
    </svg>
  );
}
